import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import DatePicker from 'react-datepicker';
import moment from 'moment/moment';
import fetch from 'isomorphic-fetch';
import { format } from 'date-fns-tz';
import { parseJSON, slugify } from '../../utils';
import { galleryTypes } from './SnapshotPageVariants';
import { ChannelsSubmenu, ContestsSubmenu, PartnersSubmenu } from './Submenus';
import { fetchChannels } from '../../actions/channels';
import { setCurrentSection } from '../../actions/globalConfig';
import TimezonePicker from '../../components/TimezonePicker';
import SnapshotGalleryV3 from '../../components/Snapshots/SnapshotGallery';
import { SnapshotGalleryContext } from '../../contexts/SnapshotGalleryContext';
import './snapshots.scss';

const SnapshotTotal = ({ queryType, queryId, createdBefore }) => {
  const q = queryString.stringify({ id: queryId, created_before: createdBefore });
  const countFetch = async () => {
    return await fetch(`${queryType}?${q}&total=true`)
      .then(parseJSON);
  };
  const { isPending, isError, data } = useQuery({
    queryKey: [queryType, queryId, 'count'],
    queryFn: countFetch,
  });

  const formatNumber = (number) => {
    if (Number.isNaN(number)) {
      return '--';
    }
    return new Intl.NumberFormat().format(number);
  };
  return (<h4>{(isPending || isError) ? '--' : formatNumber(data?.data?.count)} Snapshots</h4>);
};

const SnapshotView = ({
  currentUsername,
  isAuthenticated,
  currentUser,
  type,
  slug,
  title,
  description,
  avatarUri,
  contestStatus,
  sectionName,
  location,
  channels,
  dispatch,
  router,
}) => {
  const { queryType, queryId } = useContext(SnapshotGalleryContext);
  const [sidebarActive, setSidebarActive] = useState(true);
  const [inputDate, setInputDate] = useState(new Date());
  const userTz = moment.tz.guess();
  const [selectedTimezone, setSelectedTimezone] = useState(userTz);
  const [dateString, setDateString] = useState('');
  const toggleSidebar = () => {
    setSidebarActive(!sidebarActive);
  };

  useEffect(() => {
    if (!channels || channels.length === 0) {
      dispatch(fetchChannels());
    }
    dispatch(setCurrentSection(sectionName));
    const queryParams = queryString.parse(_.get(location, 'search'));
    if (queryParams.user) {
      const user = queryParams.user;
      delete queryParams.user;
      const newQueryString = queryString.stringify(queryParams);
      router.push(`/snapshots/users/${user}?${newQueryString}`);
    }
  }, []);

  useEffect(() => {
    if (inputDate) {
      const inputDateString = format(inputDate, 'yyyy-MM-dd\'T\'HH:mm:ss');
      const timezoneOffsetString = format(new Date(), 'xxx', { timeZone: selectedTimezone });
      const formattedDate = `${inputDateString}${timezoneOffsetString}`;
      setDateString(formattedDate);
    }
  }, [inputDate, selectedTimezone]);

  return (
    <div id="main-snapshot-wrap" className="snapshots-wrapper">
      <nav className={sidebarActive ? 'snapshots-sidebar active' : 'snapshots-sidebar'}>
        <div className="sidebar-header">
          <h3>Snapshots</h3>
        </div>
        <ul className="list-unstyled components pt-0">
          {currentUsername && (
            <div>
              <li className={type === galleryTypes.USER && slug === currentUser ? 'active' : ''}>
                <Link to={`/snapshots/users/${currentUsername}`}>My Gallery</Link>
              </li>
              <li className={type === galleryTypes.FAVORITE && slug === currentUser ? 'active' : ''}>
                <Link to={`/snapshots/favorites/${currentUsername}`}>My Favorites</Link>
              </li>
            </div>
          )}
          <li className={type === galleryTypes.ALL ? 'active' : ''}>
            <Link to="/snapshots">All Snapshots</Link>
          </li>
          <li className={slug === 'dj-of-nature-favorites' ? 'active' : ''}>
            <Link to="/snapshots/dj-of-nature-favorites">DJ of Nature&apos;s Favorites</Link>
          </li>
          <li className={slug === 'favorites' ? 'active' : ''}>
            <Link to="/snapshots/favorites">Fan Favorites</Link>
          </li>
          <li className={slug === 'camops' ? 'active' : ''}>
            <Link to="/snapshots/camops">CamOp Snaps</Link>
          </li>
          <ContestsSubmenu slug={slug} />
          <PartnersSubmenu slug={slug} />
          <ChannelsSubmenu slug={slug} />
        </ul>
      </nav>
      <div id="content">
        <div className="container-fluid p-4">
          <div className="row pb-4 align-items-baseline">
            <div className="col-auto">
              <button
                type="button"
                id="sidebarCollapse"
                className="btn clickable btn-outline-dark"
                onClick={toggleSidebar}
              >
                <i className="fas fa-align-left" />
              </button>
            </div>
            <div className="col-12 col-md">
              {avatarUri && (
                <span
                  className="mr-3 snapshot-avatar"
                  style={{ backgroundImage: `url('${avatarUri}')` }}
                />
              )}
              <h2 className="d-inline-block mb-3 mb-md-0 mt-3 mt-md-0 mr-2" style={{ verticalAlign: 'middle' }}>
                {title}
              </h2>
              {type === galleryTypes.CONTEST &&
                <div className="alert alert-success small-alert"><i className="fa fa-trophy mr-2" /> Snapshot Contest
                </div>}
              {type === galleryTypes.CONTEST && <span className="ml-2">{contestStatus}</span>}
            </div>
            <div className="col-12 col-md-auto ml-auto">
              {queryType && <SnapshotTotal queryType={queryType} queryId={queryId} createdBefore={dateString} />}
            </div>
          </div>
          <div className="gallery-meta-row pb-4">
            <div className="date-info">
              <div className="no-outline" style={{ minWidth: 315 }}>
                <i
                  className="d-block fa-2x fa-calendar-alt far float-left mr-2 pt-1 clickable"
                  role="button"
                  tabIndex={0}
                />
                <span
                  className="clickable"
                  role="button"
                  tabIndex={0}

                >Snapshots taken on or before</span>
                <div className="align-items-baseline row no-gutters">
                  <div className="col-auto datepicker-wrap">
                    <DatePicker
                      className="plain-input clickable"
                      showTimeSelect
                      selected={inputDate}
                      onChange={date => setInputDate(date)}
                      dateFormat="MMM d, yyyy h:mm aa"
                      disabledKeyboardNavigation
                    />
                  </div>
                  <div className="col">
                    <TimezonePicker
                      onChange={(timezone) => {
                        setSelectedTimezone(timezone);
                      }}
                      inputDate={inputDate}
                      initialValue={userTz}
                      inputClassName="plain-input clickable"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="description">
              <p>{description}</p>
            </div>
          </div>
          {queryType && <SnapshotGalleryV3 createdBefore={dateString} />}
        </div>
      </div>
    </div>
  );
};

SnapshotView.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  avatarUri: PropTypes.string,
  slug: PropTypes.string,
  type: PropTypes.string.isRequired,
  contestStatus: PropTypes.string,
};

SnapshotView.defaultProps = {
  title: '',
  description: '',
  avatarUri: '',
  slug: '',
  contestStatus: null,
};

const mapStateToPops = (state) => {
  const { favorites, auth, currentUser } = state;
  let channels = [];
  if (state.channels.data) {
    channels = _.sortBy(state.channels.data.map((channel) => {
      return {
        title: channel.title,
        slug: slugify(channel.title),
        id: channel.id,
      };
    }), 'title');
  }
  return {
    channels,
    favoriteSnapshots: _.get(favorites, 'user.snapshots', []) || [],
    currentUsername: _.get(auth, 'userName', null),
    currentUser: _.get(currentUser, 'data', {}),
    isAuthenticated: state.auth.isAuthenticated,
  };
};

export default connect(mapStateToPops)(SnapshotView);
